import { createStore } from 'zustand/vanilla';

const createCompanyStore = (supabaseClient, getUserId) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required to initialize the company store');
  }

  if (!getUserId || typeof getUserId !== 'function') {
    throw new Error('getUserId function is required to initialize the company store');
  }

  try {
    const store = createStore((set, get) => ({
      companies: [],
      filteredCompanies: [],
      isLoading: false,
      error: null,
      isConnected: false,
      filters: {
        searchTerm: '',
        city: '',
        department: '',
        status: '',
      },
      sortBy: 'razon_social-asc',

      setConnectionStatus: (status) => set({ isConnected: status }),

      fetchCompanies: async () => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('rut_contribuyentes')
            .select('*')
            .eq('user_id', userId)
            .order('razon_social', { ascending: true });

          if (error) throw error;

          set({
            companies: data || [],
            isLoading: false,
            isConnected: true,
          });
          get().applyFiltersAndSort();
        } catch (error) {
          console.error('Error fetching companies:', error.message);
          set({
            error: 'Failed to load company records. Please try again later.',
            isLoading: false,
            isConnected: false,
          });
        }
      },

      updateCompanyStatus: async (companyId, status) => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('rut_contribuyentes')
            .update({ estado: status })
            .eq('id', companyId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            companies: state.companies.map((company) =>
              company.id === companyId ? { ...company, estado: status } : company
            ),
            isLoading: false,
          }));
          get().applyFiltersAndSort();
          return { success: true };
        } catch (error) {
          console.error('Error updating company status:', error.message);
          set({
            error: 'Failed to update company status. Please try again later.',
            isLoading: false,
          });
          return { success: false, error };
        }
      },

      addCompany: async (companyId) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('rut_contribuyentes')
            .select('*')
            .eq('id', companyId)
            .eq('user_id', userId)
            .single();

          if (error) throw error;

          if (data) {
            set((state) => ({
              companies: [data, ...state.companies],
            }));
            get().applyFiltersAndSort();
          }
        } catch (error) {
          console.error('Error adding new company:', error.message);
        }
      },

      updateCompany: async (companyId) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('rut_contribuyentes')
            .select('*')
            .eq('id', companyId)
            .eq('user_id', userId)
            .single();

          if (error) throw error;

          if (data) {
            set((state) => ({
              companies: state.companies.map((company) =>
                company.id === companyId ? data : company
              ),
            }));
            get().applyFiltersAndSort();
          }
        } catch (error) {
          console.error('Error updating company:', error.message);
        }
      },

      deleteCompany: (companyId) => {
        set((state) => ({
          companies: state.companies.filter((company) => company.id !== companyId),
        }));
        get().applyFiltersAndSort();
      },

      setFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        }));
        get().applyFiltersAndSort();
      },

      resetFilters: () => {
        set({
          filters: {
            searchTerm: '',
            city: '',
            department: '',
            status: '',
          },
        });
        get().applyFiltersAndSort();
      },

      setSort: (sortOption) => {
        set({ sortBy: sortOption });
        get().applyFiltersAndSort();
      },

      applyFiltersAndSort: () => {
        const { companies, filters, sortBy } = get();

        if (!companies || companies.length === 0) {
          set({ filteredCompanies: [] });
          return;
        }

        let filtered = [...companies];

        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filtered = filtered.filter((company) =>
            (company.razon_social && company.razon_social.toLowerCase().includes(searchLower)) ||
            (company.nombre_comercial && company.nombre_comercial.toLowerCase().includes(searchLower)) ||
            (company.nit && company.nit.includes(filters.searchTerm))
          );
        }

        if (filters.city) {
          filtered = filtered.filter((company) =>
            company.ciudad && company.ciudad.toLowerCase() === filters.city.toLowerCase()
          );
        }

        if (filters.department) {
          filtered = filtered.filter((company) =>
            company.departamento && company.departamento.toLowerCase() === filters.department.toLowerCase()
          );
        }

        if (filters.status) {
          filtered = filtered.filter((company) =>
            company.estado && company.estado.toLowerCase() === filters.status.toLowerCase()
          );
        }

        const [sortField, sortDirection] = sortBy.split('-');
        filtered.sort((a, b) => {
          let valueA = a[sortField] || '';
          let valueB = b[sortField] || '';

          if (typeof valueA === 'string') {
            valueA = valueA.toLowerCase();
            valueB = valueB.toLowerCase();
          }

          if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
          if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });

        set({ filteredCompanies: filtered });
      },
    }));

    return store;
  } catch (error) {
    console.error('Error creating company store:', error);
    throw error;
  }
};

// Export predeterminado: función que crea el store
export default createCompanyStore;